import React, { useState, useEffect, useCallback, useMemo } from 'react';
import UserLink from './UserLink';
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Cell,
} from 'recharts';
import {
  AlertCircle, Clock, UserX, Timer, Flame,
  TrendingUp, TrendingDown, Minus, RefreshCw, MessageSquare, CheckCircle, Plus,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const PV = 'rgb(var(--pv-800))';

// Priority and status colours are lifted from the badge palette in App.js on
// purpose. A ticket that reads amber in the list should not read blue in a
// chart -- the whole value of a dashboard is that you can glance between it and
// the queue without re-learning what the colours mean.
const PRIORITY_COLORS = {
  urgent: '#dc2626',
  high:   '#ea580c',
  medium: '#2563eb',
  low:    '#64748b',
};

const AGING_COLORS = ['#22c55e', '#84cc16', '#eab308', '#f97316', '#dc2626'];

const DOW_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// ---------------------------------------------------------------------------
// Small shared pieces
// ---------------------------------------------------------------------------

const Panel = ({ title, hint, children, className = '', action = null, fill = false }) => (
  <div className={`bg-surface rounded-xl border border-n-200 ${fill ? 'h-full flex flex-col' : ''} ${className}`}>
    <div className="flex items-start justify-between gap-4 px-5 pt-4 pb-3">
      <div>
        <h3 className="text-sm font-semibold text-n-900">{title}</h3>
        {hint && <p className="text-xs text-n-500 mt-0.5">{hint}</p>}
      </div>
      {action}
    </div>
    {/* min-h-0 is load-bearing: without it a flex child refuses to shrink below
        its content height and the inner overflow-y-auto never scrolls. */}
    <div className={`px-5 pb-5 ${fill ? 'flex-1 min-h-0 flex flex-col' : ''}`}>{children}</div>
  </div>
);

const Empty = ({ children }) => (
  <div className="flex-1 h-full min-h-[8rem] flex items-center justify-center text-sm text-n-500">
    {children}
  </div>
);

// Counters across the top. Deliberately flat -- no gradient, no lift on hover.
// These get looked at every morning; the one that should catch your eye is the
// one with a number in it, not the one with the nicest border.
const Counter = ({ label, value, icon: Icon, tone = 'neutral', onClick, sub }) => {
  const tones = {
    neutral: 'text-n-900',
    red:     'text-red-700 dark:text-red-400',
    amber:   'text-yellow-700 dark:text-yellow-400',
    green:   'text-green-700 dark:text-green-400',
  };
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!onClick}
      className="bg-surface border border-n-200 rounded-xl px-4 py-3 text-left transition-colors hover:bg-n-100 disabled:hover:bg-surface disabled:cursor-default focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1"
      style={{ '--tw-ring-color': PV }}
    >
      <div className="flex items-center gap-1.5 text-n-600">
        {Icon && <Icon className="w-3.5 h-3.5" />}
        <span className="text-xs font-medium">{label}</span>
      </div>
      <div className={`mt-1 text-3xl font-semibold tabular-nums ${tones[tone]}`}>{value}</div>
      {sub && <div className="text-xs text-n-500 mt-0.5">{sub}</div>}
    </button>
  );
};

const ChartTooltip = ({ active, payload, label, unit = '' }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="bg-surface border border-n-300 rounded-lg px-3 py-2 shadow-lg text-xs">
      {label !== undefined && <div className="font-medium text-n-900 mb-1">{label}</div>}
      {payload.map((p) => (
        <div key={p.dataKey ?? p.name} className="flex items-center gap-2 text-n-700">
          <span className="w-2 h-2 rounded-sm" style={{ backgroundColor: p.color || p.fill }} />
          <span>{p.name}</span>
          <span className="font-semibold tabular-nums text-n-900">{p.value}{unit}</span>
        </div>
      ))}
    </div>
  );
};

const formatHours = (h) => {
  if (h === null || h === undefined) return '--';
  if (h < 1) return `${Math.round(h * 60)}m`;
  if (h < 48) return `${h.toFixed(1)}h`;
  return `${(h / 24).toFixed(1)}d`;
};

const relative = (value) => {
  const then = new Date(value);
  if (isNaN(then)) return '';
  const mins = Math.floor((Date.now() - then) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return then.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const ageInDays = (value) => Math.floor((Date.now() - new Date(value)) / 86400000);

// ---------------------------------------------------------------------------
// Dashboard
// ---------------------------------------------------------------------------

const Dashboard = ({ token, user, onOpenTicket, onNavigate, onOpenUser }) => {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async (silent = false) => {
    if (!token) return;
    if (silent) setRefreshing(true); else setLoading(true);
    try {
      const res = await fetch(`${API_URL}/dashboard/stats?days=${days}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Dashboard request failed (${res.status})`);
      setData(await res.json());
      setError('');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [token, days]);

  useEffect(() => { load(); }, [load]);

  // Every load runs eleven aggregate queries, so this deliberately does not
  // match the ticket list's 60s poll. Nothing here moves fast enough to be
  // worth that: a median resolution time doesn't change minute to minute. Five
  // minutes in the background, plus an immediate refresh when you come back to
  // the tab, which is when you actually look at it.
  useEffect(() => {
    if (!token) return;
    const id = setInterval(() => load(true), 300000);
    return () => clearInterval(id);
  }, [token, load]);

  useEffect(() => {
    if (!token) return;
    const onFocus = () => {
      if (document.visibilityState === 'visible') load(true);
    };
    document.addEventListener('visibilitychange', onFocus);
    return () => document.removeEventListener('visibilitychange', onFocus);
  }, [token, load]);

  const openTicket = async (id) => {
    try {
      const res = await fetch(`${API_URL}/tickets/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Could not open that ticket');
      onOpenTicket(await res.json());
    } catch (err) {
      setError(err.message);
    }
  };

  const series = useMemo(() => (data?.series || []).map((row) => ({
    ...row,
    label: new Date(`${row.date}T12:00:00`).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
  })), [data]);

  const priorityData = useMemo(() => {
    const p = data?.byPriority || {};
    return ['urgent', 'high', 'medium', 'low']
      .map((k) => ({ name: k[0].toUpperCase() + k.slice(1), key: k, count: p[k] || 0 }))
      .filter((row) => row.count > 0);
  }, [data]);

  const agingData = useMemo(() => {
    const a = data?.aging;
    if (!a) return [];
    return [
      { name: 'Under a day', count: a.d0 },
      { name: '1-3 days',    count: a.d1 },
      { name: '3-7 days',    count: a.d3 },
      { name: '1-2 weeks',   count: a.d7 },
      { name: 'Over 2 weeks', count: a.d14 },
    ];
  }, [data]);

  const heat = useMemo(() => {
    const rows = data?.heatmap || [];
    const grid = {};
    let max = 0;
    rows.forEach(({ dow, hour, count }) => {
      grid[`${dow}-${hour}`] = count;
      if (count > max) max = count;
    });
    return { grid, max };
  }, [data]);

  if (loading && !data) {
    return (
      <div className="space-y-4">
        <div className="h-8 w-64 rounded bg-n-100" />
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          {[0, 1, 2, 3, 4].map((i) => <div key={i} className="h-24 rounded-xl bg-n-100" />)}
        </div>
        <div className="h-72 rounded-xl bg-n-100" />
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="bg-surface border border-n-200 rounded-xl p-8 text-center">
        <h3 className="text-base font-semibold text-n-900">The dashboard couldn't load</h3>
        <p className="text-sm text-n-600 mt-1">{error}</p>
        <button
          onClick={() => load()}
          className="mt-4 px-4 py-2 rounded-lg text-white text-sm font-medium"
          style={{ backgroundColor: PV }}
        >
          Try again
        </button>
      </div>
    );
  }

  const t = data.totals;
  const p = data.period;
  const net = p.net;
  const createdDelta = p.created - p.createdPrev;

  return (
    <div className="space-y-4">

      {/* Header ---------------------------------------------------------- */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-n-900">
            {user?.name ? `Good to see you, ${user.name.split(' ')[0]}` : 'Help desk'}
          </h2>
          <p className="text-sm text-n-600 mt-1">
            {t.open + t.inProgress} tickets in the queue
            {t.createdToday > 0 && ` · ${t.createdToday} in today`}
            {t.resolvedToday > 0 && ` · ${t.resolvedToday} closed today`}
          </p>
        </div>

        <div className="flex items-center gap-2">
          <div className="flex rounded-lg border border-n-200 overflow-hidden">
            {[7, 30, 90].map((d) => (
              <button
                key={d}
                onClick={() => setDays(d)}
                className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                  days === d ? 'text-white' : 'text-n-600 hover:bg-n-100'
                }`}
                style={days === d ? { backgroundColor: PV } : undefined}
              >
                {d} days
              </button>
            ))}
          </div>
          <button
            onClick={() => load(true)}
            title="Refresh"
            className="p-2 rounded-lg border border-n-200 text-n-600 hover:bg-n-100 transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {error && (
        <div className="text-xs text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30 rounded-lg px-3 py-2">
          {error} — showing the last successful load.
        </div>
      )}

      {/* Counters -------------------------------------------------------- */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <Counter
          label="Open" value={t.open} icon={AlertCircle} tone="red"
          onClick={() => onNavigate('tickets', { status: 'open', sortBy: 'date' })}
        />
        <Counter
          label="In progress" value={t.inProgress} icon={Clock} tone="amber"
          onClick={() => onNavigate('tickets', { status: 'in-progress', sortBy: 'date' })}
        />
        <Counter
          label="Unassigned" value={t.unassigned} icon={UserX}
          tone={t.unassigned > 0 ? 'red' : 'neutral'}
          sub={t.unassigned > 0 ? 'nobody picked these up' : 'all claimed'}
          onClick={() => onNavigate('tickets', { status: 'unresolved', assigned: 'unassigned', sortBy: 'date' })}
        />
        <Counter
          label="Open over a week" value={t.stale} icon={Timer}
          tone={t.stale > 0 ? 'amber' : 'green'}
          onClick={() => onNavigate('tickets', { status: 'all', sortBy: 'date' })}
        />
        <Counter
          label="Urgent or high" value={t.urgent + t.high} icon={Flame}
          tone={t.urgent > 0 ? 'red' : 'neutral'}
          sub={t.urgent > 0 ? `${t.urgent} urgent` : null}
          onClick={() => onNavigate('tickets', { status: 'all', sortBy: 'status' })}
        />
      </div>

      {/* Are we keeping up? ---------------------------------------------- */}
      <Panel
        title="Incoming against resolved"
        hint={`Last ${data.days} days`}
        action={
          <div className="text-right">
            <div className="flex items-center gap-1.5 justify-end">
              {net > 0 ? <TrendingUp className="w-4 h-4 text-red-600" />
                : net < 0 ? <TrendingDown className="w-4 h-4 text-green-600" />
                : <Minus className="w-4 h-4 text-n-400" />}
              <span className={`text-lg font-semibold tabular-nums ${
                net > 0 ? 'text-red-700 dark:text-red-400'
                  : net < 0 ? 'text-green-700 dark:text-green-400' : 'text-n-700'
              }`}>
                {net > 0 ? `+${net}` : net}
              </span>
            </div>
            <div className="text-xs text-n-500">
              {net > 0 ? 'queue grew' : net < 0 ? 'queue shrank' : 'holding even'}
            </div>
          </div>
        }
      >
        <div className="h-64 text-n-400">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={series} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="dashCreated" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#ef4444" stopOpacity={0.25} />
                  <stop offset="100%" stopColor="#ef4444" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="dashResolved" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={PV} stopOpacity={0.25} />
                  <stop offset="100%" stopColor={PV} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="currentColor" opacity={0.25} vertical={false} />
              <XAxis dataKey="label" stroke="currentColor" fontSize={11} tickLine={false} axisLine={false} minTickGap={24} />
              <YAxis stroke="currentColor" fontSize={11} tickLine={false} axisLine={false} allowDecimals={false} />
              <Tooltip content={<ChartTooltip />} />
              <Area type="monotone" dataKey="created" name="Created" stroke="#ef4444" strokeWidth={2} fill="url(#dashCreated)" />
              <Area type="monotone" dataKey="resolved" name="Resolved" stroke={PV} strokeWidth={2} fill="url(#dashResolved)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <div className="flex flex-wrap items-center gap-x-6 gap-y-1 mt-3 pt-3 border-t border-n-200 text-xs text-n-600">
          <span className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-sm bg-red-500" /> Created
            <span className="font-semibold text-n-900 tabular-nums">{p.created}</span>
            <span className="text-n-400">
              ({createdDelta >= 0 ? '+' : ''}{createdDelta} vs previous {data.days} days)
            </span>
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: PV }} /> Resolved
            <span className="font-semibold text-n-900 tabular-nums">{p.resolved}</span>
          </span>
        </div>
      </Panel>

      {/* Aging + resolution time ----------------------------------------- */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Panel
          title="How long the queue has been waiting"
          hint="Open and in-progress, by age"
          className="lg:col-span-2"
        >
          {agingData.every((r) => r.count === 0) ? (
            <Empty>Nothing open. Enjoy it.</Empty>
          ) : (
            <div className="h-48 text-n-400">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={agingData} layout="vertical" margin={{ top: 0, right: 16, left: 8, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="currentColor" opacity={0.25} horizontal={false} />
                  <XAxis type="number" stroke="currentColor" fontSize={11} tickLine={false} axisLine={false} allowDecimals={false} />
                  <YAxis type="category" dataKey="name" stroke="currentColor" fontSize={11} tickLine={false} axisLine={false} width={92} />
                  <Tooltip content={<ChartTooltip />} cursor={{ fill: 'currentColor', opacity: 0.06 }} />
                  <Bar dataKey="count" name="Tickets" radius={[0, 4, 4, 0]}>
                    {agingData.map((row, i) => <Cell key={row.name} fill={AGING_COLORS[i]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </Panel>

        <Panel title="Time to resolve" hint={`Resolved in the last ${data.days} days`}>
          {data.resolution.sample === 0 ? (
            <Empty>Nothing resolved in this window.</Empty>
          ) : (
            <div className="space-y-4 pt-1">
              <div>
                <div className="text-xs text-n-600">Median</div>
                <div className="text-3xl font-semibold text-n-900 tabular-nums">
                  {formatHours(data.resolution.medianHours)}
                </div>
                <p className="text-xs text-n-500 mt-1">Half of tickets close faster than this.</p>
              </div>
              <div className="pt-3 border-t border-n-200">
                <div className="text-xs text-n-600">Average</div>
                <div className="text-xl font-semibold text-n-700 tabular-nums">
                  {formatHours(data.resolution.avgHours)}
                </div>
                <p className="text-xs text-n-500 mt-1">
                  Across {data.resolution.sample} tickets. A few long-running ones pull this well above the median.
                </p>
              </div>
            </div>
          )}
        </Panel>
      </div>

      {/* Team load + priority -------------------------------------------- */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Panel title="Who's carrying what" hint="Tickets not yet resolved" className="lg:col-span-2">
            {(data.byAssignee || []).length === 0 ? (
              <Empty>No open tickets to assign.</Empty>
            ) : (
              <div className="space-y-2 pt-1">
                {data.byAssignee.map((row) => {
                  const max = Math.max(...data.byAssignee.map((r) => r.active), 1);
                  const unassigned = row.name === 'Unassigned';
                  return (
                    <div key={row.name} className="flex items-center gap-3">
                      <div className={`w-36 shrink-0 text-xs truncate ${unassigned ? 'text-n-400 italic' : 'text-n-700'}`}>
                        <UserLink id={row.user_id} name={row.name} viewer={user} onOpen={onOpenUser} />
                      </div>
                      <div className="flex-1 h-6 flex items-center gap-0.5">
                        <div
                          className="h-full rounded-l"
                          style={{ width: `${(row.open / max) * 100}%`, backgroundColor: unassigned ? '#94a3b8' : '#ef4444' }}
                          title={`${row.open} open`}
                        />
                        <div
                          className="h-full rounded-r"
                          style={{ width: `${(row.in_progress / max) * 100}%`, backgroundColor: unassigned ? '#cbd5e1' : '#eab308' }}
                          title={`${row.in_progress} in progress`}
                        />
                      </div>
                      <div className="w-8 shrink-0 text-right text-xs font-semibold text-n-900 tabular-nums">
                        {row.active}
                      </div>
                    </div>
                  );
                })}
                <div className="flex items-center gap-4 pt-2 mt-2 border-t border-n-200 text-xs text-n-600">
                  <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-red-500" /> Open</span>
                  <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-yellow-500" /> In progress</span>
                </div>
            </div>
          )}
        </Panel>

        <Panel title="Open work by priority">
          {priorityData.length === 0 ? (
            <Empty>Nothing open.</Empty>
          ) : (
            <div className="space-y-2 pt-1">
              {priorityData.map((row) => {
                const total = priorityData.reduce((s, r) => s + r.count, 0);
                return (
                  <div key={row.key}>
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="text-n-700">{row.name}</span>
                      <span className="font-semibold text-n-900 tabular-nums">{row.count}</span>
                    </div>
                    <div className="h-2 rounded-full bg-n-100 overflow-hidden">
                      <div
                        className="h-full rounded-full"
                        style={{ width: `${(row.count / total) * 100}%`, backgroundColor: PRIORITY_COLORS[row.key] }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Panel>
      </div>

      {/* Where and when tickets come from --------------------------------- */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Panel title="Busiest locations" hint={`Tickets raised in the last ${data.days} days`}>
            {(data.byLocation || []).length === 0 ? (
              <Empty>No tickets in this window.</Empty>
            ) : (
              <div className="h-56 text-n-400">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.byLocation} layout="vertical" margin={{ top: 0, right: 16, left: 8, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="currentColor" opacity={0.25} horizontal={false} />
                    <XAxis type="number" stroke="currentColor" fontSize={11} tickLine={false} axisLine={false} allowDecimals={false} />
                    <YAxis type="category" dataKey="location" stroke="currentColor" fontSize={11} tickLine={false} axisLine={false} width={110} />
                    <Tooltip content={<ChartTooltip />} cursor={{ fill: 'currentColor', opacity: 0.06 }} />
                    <Bar dataKey="count" name="Tickets" fill={PV} radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </Panel>

          <Panel title="When tickets arrive" hint="School week, 6am to 6pm">
            {heat.max === 0 ? (
              <Empty>No tickets in this window.</Empty>
            ) : (
              <div className="pt-1 overflow-x-auto">
                <div className="min-w-[22rem]">
                  <div className="flex gap-px mb-1 pl-9">
                    {Array.from({ length: 13 }, (_, i) => i + 6).map((h) => (
                      <div key={h} className="flex-1 text-center text-[10px] text-n-500 tabular-nums">
                        {h % 3 === 0 ? (h > 12 ? h - 12 : h) : ''}
                      </div>
                    ))}
                  </div>
                  {[1, 2, 3, 4, 5].map((dow) => (
                    <div key={dow} className="flex gap-px items-center mb-px">
                      <div className="w-9 shrink-0 text-[10px] text-n-500">{DOW_LABELS[dow]}</div>
                      {Array.from({ length: 13 }, (_, i) => i + 6).map((h) => {
                        const count = heat.grid[`${dow}-${h}`] || 0;
                        const intensity = count === 0 ? 0 : 0.15 + (count / heat.max) * 0.85;
                        return (
                          <div
                            key={h}
                            className="flex-1 h-6 rounded-sm border border-n-200"
                            style={{ backgroundColor: count === 0 ? 'transparent' : `rgb(var(--pv-800) / ${intensity})` }}
                            title={`${DOW_LABELS[dow]} ${h}:00 — ${count} ticket${count === 1 ? '' : 's'}`}
                          />
                        );
                      })}
                    </div>
                  ))}
                  <p className="text-xs text-n-500 mt-3">
                    Darker means more tickets. Peak is {heat.max} in a single hour.
                  </p>
                </div>
              </div>
            )}
        </Panel>
      </div>

      {/* Oldest open + activity ------------------------------------------- */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:h-[30rem]">
        <Panel
          fill
          title="Waiting longest"
          hint="Oldest tickets still open"
          action={
            <button
              onClick={() => onNavigate('tickets', { status: 'all', sortBy: 'date' })}
              className="text-xs font-medium hover:underline"
              style={{ color: PV }}
            >
              All tickets
            </button>
          }
        >
          {(data.oldestOpen || []).length === 0 ? (
            <Empty>Nothing open.</Empty>
          ) : (
            <div className="-mx-2 flex-1 min-h-0 max-h-[26rem] overflow-y-auto divide-y divide-n-200">
              {data.oldestOpen.map((ticket) => {
                const age = ageInDays(ticket.created_at);
                return (
                  <button
                    key={ticket.id}
                    onClick={() => openTicket(ticket.id)}
                    className="w-full text-left px-2 py-2.5 hover:bg-n-100 transition-colors"
                  >
                    <div className="flex items-baseline gap-2 min-w-0">
                      <span className="text-xs text-n-400 tabular-nums shrink-0">#{ticket.id}</span>
                      <span className="text-sm text-n-900 truncate flex-1">{ticket.title}</span>
                      <span className={`text-xs font-semibold tabular-nums shrink-0 ${
                        age >= 14 ? 'text-red-700 dark:text-red-400'
                          : age >= 7 ? 'text-orange-700 dark:text-orange-400' : 'text-n-600'
                      }`}>
                        {age}d
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5 mt-0.5 text-xs text-n-500 truncate">
                      <span
                        className="px-1.5 py-0.5 rounded text-[10px] font-medium"
                        style={{ backgroundColor: `${PRIORITY_COLORS[ticket.priority]}22`, color: PRIORITY_COLORS[ticket.priority] }}
                      >
                        {ticket.priority}
                      </span>
                      {ticket.location && <span className="truncate">{ticket.location}</span>}
                      <span className={ticket.assigned_to_name ? '' : 'italic text-n-400'}>
                        <UserLink
                          id={ticket.assigned_to}
                          name={ticket.assigned_to_name}
                          viewer={user}
                          onOpen={onOpenUser}
                          fallback="unassigned"
                        />
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </Panel>

        <Panel title="Recent activity" fill>
          {(data.activity || []).length === 0 ? (
            <Empty>Nothing has happened yet.</Empty>
          ) : (
            <div className="-mx-2 flex-1 min-h-0 max-h-[26rem] overflow-y-auto">
              {data.activity.map((ev, i) => {
                const Icon = ev.kind === 'created' ? Plus
                  : ev.kind === 'resolved' ? CheckCircle : MessageSquare;
                const tone = ev.kind === 'created' ? 'text-blue-600 dark:text-blue-400'
                  : ev.kind === 'resolved' ? 'text-green-600 dark:text-green-400'
                  : 'text-n-500';
                const verb = ev.kind === 'created' ? 'opened'
                  : ev.kind === 'resolved' ? 'resolved' : 'commented on';
                return (
                  <button
                    key={`${ev.kind}-${ev.ticket_id}-${i}`}
                    onClick={() => openTicket(ev.ticket_id)}
                    className="w-full text-left flex items-start gap-2.5 px-2 py-2 hover:bg-n-100 transition-colors rounded"
                  >
                    <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${tone}`} />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-n-700 truncate">
                        <UserLink
                          id={ev.actor_id}
                          name={ev.actor}
                          viewer={user}
                          onOpen={onOpenUser}
                          fallback="Someone"
                          className="font-medium text-n-900"
                        />
                        {' '}{verb}{' '}
                        <span className="text-n-500">#{ev.ticket_id}</span>
                        {' '}{ev.title}
                      </p>
                      <p className="text-xs text-n-500">{relative(ev.event_at)}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </Panel>
      </div>

      <p className="text-xs text-n-400 text-center pt-2">
        Updated {new Date(data.generated_at).toLocaleTimeString()} · refreshes every minute
      </p>
    </div>
  );
};

export default Dashboard;
